import { createReducer } from '@reduxjs/toolkit';
import type { Reducer } from '@reduxjs/toolkit';
import type { SvpAssetId } from '@vgtv/api-client/lib/svp_asset';
import type { SvpCategoryId } from '@vgtv/api-client/lib/svp_category';

import type { AssetWatchtime } from '../../services/watchtimeApi';
import type { Vendor } from '../../types';

import {
  upsertAssetWatchtime,
  fetchWatchtimeHistory,
  fetchWatchtimes,
  saveAssetWatchtime,
  fetchSeriesWatchtimes,
} from './actions';

export * from './selectors';

export interface WatchtimeState {
  history: AssetWatchtime[];
  items: Record<Vendor, Partial<Record<SvpAssetId, AssetWatchtime>>>;
  series: Record<Vendor, Partial<Record<SvpCategoryId, SvpAssetId>>>;
  savedAt?: number;
  updatedAt?: number;
}

const initialState: WatchtimeState = {
  history: [],
  items: { vgtv: {}, ab: {}, brandstudio: {} },
  series: { vgtv: {}, ab: {}, brandstudio: {} },
  savedAt: undefined,
  updatedAt: undefined,
};

/**
 * It requires explicit typing because of issue described in {@link https://github.com/reduxjs/redux-toolkit/issues/653#issuecomment-677521690}
 */
export const watchtimeReducer: Reducer<typeof initialState> = createReducer(
  initialState,
  (builder) =>
    builder
      .addCase(fetchWatchtimeHistory.fulfilled, (state, action) => {
        const { vendor } = action.meta.arg;
        state.history = action.payload;

        action.payload.forEach((item) => {
          state.items[vendor][item.assetId] = item;
        });
      })
      .addCase(fetchWatchtimes.fulfilled, (state, action) => {
        const { vendor } = action.meta.arg;

        action.payload.forEach((item) => {
          state.items[vendor][item.assetId] = item;
        });
      })
      .addCase(saveAssetWatchtime.pending, (state) => {
        state.savedAt = Date.now();
      })
      .addCase(upsertAssetWatchtime, (state, action) => {
        const { vendor, data } = action.payload;

        const watchtime = state.items[vendor][data.assetId];

        if (watchtime) {
          watchtime.time = data.time;
          watchtime.updatedAt = data.updatedAt;
        } else {
          state.items[vendor][data.assetId] = {
            ...data,
            isWatched: false,
          };
        }

        state.updatedAt = Date.now();
      })
      .addCase(fetchSeriesWatchtimes.fulfilled, (state, action) => {
        const { vendor } = action.meta.arg;

        action.payload.forEach(({ seriesId, watchtime }) => {
          state.items[vendor][watchtime.assetId] = watchtime;
          state.series[vendor][seriesId] = watchtime.assetId;
        });
      })
);
